import AlertBanner from 'tio-reimagined/components/alert-banner';
import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import Popover from 'tio-reimagined/components/popover';
import CurrencySuperscriptCents from '../currency-superscript-cents.gts';
import { formatISODateToDisplay } from '../../utils/date';
import type { LimitsStatusDataSignature } from '../../services/tuition-assistance';
import { service } from '@ember/service';
import type { IntlService } from 'ember-intl';
import type EmployeeModel from 'tio-data/models/employee';

const CREDITS_CAPACITY = 'TAS.LimitCapacity.CREDITS';

/**
 * External spend line item for external partner popover
 */
interface ExternalSpendItem {
  description: string;
  amount: number;
}

export interface AnnualBenefitsSignature {
  Args: {
    limitsStatus: LimitsStatusDataSignature;
    employee: EmployeeModel;
    /**
     * Optional external spend line items to display in the partner popover.
     * If not provided, we'll show a summary view.
     */
    externalSpendItems?: ExternalSpendItem[];
  };
  Element: HTMLDivElement;
}

class TasDashboardAnnualBenefits extends Component<AnnualBenefitsSignature> {
  @service declare intl: IntlService;

  // === Capacity type ===

  get isCreditBased() {
    return this.args.limitsStatus?.limit_capacity === CREDITS_CAPACITY;
  }

  /**
   * Format an amount for display based on capacity type.
   * Credits: plain integer (e.g. "12 credits")
   * Monetary: delegates to currency helper in template
   */
  formatCredits = (amount: number) => {
    return this.intl.t('tas.dashboard.annual_benefits.credits_count', { count: amount });
  };

  // === Date getters ===
  get annualStartDate() {
    return this.args.limitsStatus?.annual_start_date || '';
  }

  get annualEndDate() {
    return this.args.limitsStatus?.annual_end_date || '';
  }

  get hasDates() {
    return Boolean(this.annualStartDate && this.annualEndDate);
  }

  // === Amount calculations ===

  /**
   * Annual limit (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company)
   */
  get annualLimit() {
    const batchLimit = this.args.limitsStatus?.details?.batch?.annual_limit ?? 0;
    const schemeLimit = this.args.limitsStatus?.details?.scheme?.annual_limit ?? 0;
    const productLimit = this.args.limitsStatus?.details?.product?.annual_limit ?? 0;
    const companyLimit = this.args.limitsStatus?.details?.company?.annual_limit ?? 0;

    return Math.max(batchLimit, schemeLimit, productLimit, companyLimit);
  }

  /**
   * Net paid amount (in cents)
   *
   * Uses annual_usage from limitsStatus API as the authoritative source.
   * This includes ALL paid amounts for the benefit year.
   * Uses the highest value across all levels (batch, scheme, product, company).
   */
  get paidAmount() {
    const batchUsage = this.args.limitsStatus?.details?.batch?.annual_usage ?? 0;
    const schemeUsage = this.args.limitsStatus?.details?.scheme?.annual_usage ?? 0;
    const productUsage = this.args.limitsStatus?.details?.product?.annual_usage ?? 0;
    const companyUsage = this.args.limitsStatus?.details?.company?.annual_usage ?? 0;

    return Math.max(batchUsage, schemeUsage, productUsage, companyUsage);
  }

  /**
   * External spend from limits service (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company).
   * Includes root-level external usage when present.
   */
  get externalAmount() {
    const batchExternal = this.args.limitsStatus?.details?.batch?.external_annual_usage ?? 0;
    const schemeExternal = this.args.limitsStatus?.details?.scheme?.external_annual_usage ?? 0;
    const productExternal = this.args.limitsStatus?.details?.product?.external_annual_usage ?? 0;
    const companyExternal = this.args.limitsStatus?.details?.company?.external_annual_usage ?? 0;
    const rootExternal = this.args.limitsStatus?.external_annual_usage ?? 0;

    return Math.max(batchExternal, schemeExternal, productExternal, companyExternal, rootExternal);
  }

  /**
   * Total used = paid + external (no pending)
   */
  get totalUsed() {
    return this.paidAmount + this.externalAmount;
  }

  /**
   * Estimated remaining benefit
   * Uses the 'available' field from limitsStatus if present,
   * otherwise falls back to calculating from annualLimit - totalUsed
   */
  get remainingAmount() {
    // Use the available field from limitsStatus if present
    const available = this.args.limitsStatus?.available;
    if (available !== null && available !== undefined) {
      return Math.max(0, available);
    }

    // Fallback: calculate from annual limit - total used
    if (!this.annualLimit) {
      return 0;
    }
    return Math.max(0, this.annualLimit - this.totalUsed);
  }

  // === Progress bar percentages ===

  get hasLimit() {
    return this.annualLimit > 0;
  }

  get minVisiblePercentage() {
    return 5;
  }

  get totalUsedPercentage() {
    if (!this.hasLimit || this.totalUsed <= 0) return 0;
    const rawPercentage = (this.totalUsed / this.annualLimit) * 100;
    return Math.min(100, Math.max(this.minVisiblePercentage, rawPercentage));
  }

  get paidPercentage() {
    if (!this.hasLimit || this.paidAmount <= 0 || this.totalUsed <= 0) return 0;
    return (this.paidAmount / this.totalUsed) * this.totalUsedPercentage;
  }

  get externalPercentage() {
    if (!this.hasLimit || this.externalAmount <= 0 || this.totalUsed <= 0) return 0;
    return (this.externalAmount / this.totalUsed) * this.totalUsedPercentage;
  }

  // === Style helpers for progress bar (using htmlSafe for dynamic widths) ===

  get paidStyle() {
    return htmlSafe(`width: ${this.paidPercentage}%`);
  }

  get externalStyle() {
    return htmlSafe(`width: ${this.externalPercentage}%`);
  }

  // === Display helpers ===

  get hasAnySpend() {
    return this.totalUsed > 0;
  }

  get hasExternalSpend() {
    return this.externalAmount > 0;
  }

  get externalSpendLabel() {
    return (
      this.args.employee?.company?.companySetting?.modules?.externalSpendDescriptor ||
      this.intl.t('tas.dashboard.annual_benefits.external')
    );
  }

  get externalSpendItems(): ExternalSpendItem[] {
    // Use provided items or empty array
    return this.args.externalSpendItems || [];
  }

  get partnerName() {
    return this.args.employee?.company?.name || '';
  }

  get partnerUrlString() {
    return this.args.employee?.company?.companySetting?.partnerUrlString || '';
  }

  get hasPartnerUrl() {
    return Boolean(this.partnerUrlString);
  }

  // === Over-limit detection ===

  /**
   * Whether the employee has exceeded their benefit limit.
   * True when there IS a limit and usage exceeds it (available <= 0).
   */
  get isOverLimit() {
    if (!this.hasLimit) return false;
    const available = this.args.limitsStatus?.available;
    if (available !== null && available !== undefined) {
      return available <= 0;
    }
    return this.totalUsed >= this.annualLimit;
  }

  /**
   * The amount by which the employee has exceeded their limit.
   */
  get overLimitAmount() {
    if (!this.isOverLimit) return 0;
    return Math.max(0, this.totalUsed - this.annualLimit);
  }
  <template>
    <div class="flex flex-col gap-4" data-test-annual-benefits ...attributes>
      {{! Over-limit warning banner }}
      {{#if this.isOverLimit}}
        <AlertBanner @intent="warning" data-test-over-limit-warning>
          {{#if this.isCreditBased}}
            {{t "tas.dashboard.annual_benefits.over_limit_credits"}}
          {{else}}
            {{t "tas.dashboard.annual_benefits.over_limit"}}
          {{/if}}
        </AlertBanner>
      {{/if}}

      {{! Remaining Amount Display - different for limited vs unlimited }}
      <div class="flex flex-col gap-1">
        {{#if this.hasLimit}}
          <div class="text-3xl font-bold text-gray-900" data-test-remaining-amount>
            {{#if this.isCreditBased}}
              {{this.formatCredits this.remainingAmount}}
            {{else}}
              <CurrencySuperscriptCents @amountCents={{this.remainingAmount}} />
            {{/if}}
          </div>
          <p class="text-sm text-gray-500">
            {{#if this.isCreditBased}}
              {{t "tas.dashboard.annual_benefits.estimated_remaining_credits"}}
            {{else}}
              {{t "tas.dashboard.annual_benefits.estimated_remaining_benefit"}}
            {{/if}}
          </p>
        {{else}}
          {{! No limit - show total used instead }}
          <div class="text-3xl font-bold text-gray-900" data-test-total-used>
            {{#if this.isCreditBased}}
              {{this.formatCredits this.totalUsed}}
            {{else}}
              <CurrencySuperscriptCents @amountCents={{this.totalUsed}} />
            {{/if}}
          </div>
          <p class="text-sm text-gray-500" data-test-no-limit-label>
            {{#if this.isCreditBased}}
              {{t "tas.dashboard.annual_benefits.total_credits_used_this_year"}}
            {{else}}
              {{t "tas.dashboard.annual_benefits.total_used_this_year"}}
            {{/if}}
          </p>
        {{/if}}
      </div>

      {{! Progress Bar - only show when there's a limit }}
      {{#if this.hasLimit}}
        <div
          class="w-full h-3 bg-gray-200 rounded-full overflow-hidden flex"
          data-test-progress-bar
        >
          {{! Paid segment - dark purple }}
          {{#if this.paidPercentage}}
            <div
              class="h-full bg-violet-700"
              style={{this.paidStyle}}
              data-test-progress-paid
            ></div>
          {{/if}}
          {{! External/InStride segment - teal }}
          {{#if this.externalPercentage}}
            <div
              class="h-full bg-cyan-500"
              style={{this.externalStyle}}
              data-test-progress-external
            ></div>
          {{/if}}
          {{! Remaining segment - gray (implicit from background) }}
        </div>
      {{/if}}

      {{! Benefit Year Period + Eligible Up To }}
      {{#if this.hasDates}}
        <div class="flex items-center justify-between text-sm text-gray-600" data-test-benefit-year>
          <div>
            {{t "tas.dashboard.annual_benefits.benefit_year"}}
            <span class="font-medium">
              {{formatISODateToDisplay this.annualStartDate this.intl "2-digit"}}
              -
              {{formatISODateToDisplay this.annualEndDate this.intl "2-digit"}}
            </span>
          </div>
          {{#if this.hasLimit}}
            <div data-test-eligible-up-to>
              <span class="font-medium">
                {{t "tas.dashboard.annual_benefits.eligible_up_to"}}
              </span>
              <span class="font-semibold text-gray-700">
                {{#if this.isCreditBased}}
                  {{this.formatCredits this.annualLimit}}
                {{else}}
                  <CurrencySuperscriptCents @amountCents={{this.annualLimit}} />
                {{/if}}
              </span>
            </div>
          {{/if}}
        </div>
      {{/if}}

      {{! Legend - always show to display paid/external amounts }}
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        {{! Paid }}
        <div class="flex items-center gap-1.5" data-test-legend-paid>
          <span class="w-3 h-3 rounded-full bg-violet-700"></span>
          <span class="text-gray-700">
            {{#if this.isCreditBased}}
              {{t "tas.dashboard.annual_benefits.used"}}
            {{else}}
              {{t "tas.dashboard.annual_benefits.paid"}}
            {{/if}}
          </span>
          <span class="text-gray-500" data-test-legend-paid-amount>
            {{#if this.isCreditBased}}
              ({{this.formatCredits this.paidAmount}})
            {{else}}
              (<CurrencySuperscriptCents @amountCents={{this.paidAmount}} />)
            {{/if}}
          </span>
        </div>

        {{! External/InStride - with popover }}
        {{#if this.hasExternalSpend}}
          <Popover>
            <:trigger as |pop|>
              <button
                type="button"
                class="flex items-center gap-1.5 hover:underline cursor-pointer"
                data-test-legend-external
                {{pop.register}}
                {{on "click" pop.toggle}}
              >
                <span class="w-3 h-3 rounded-full bg-cyan-500"></span>
                <span class="text-gray-700 underline decoration-dashed underline-offset-2">
                  {{this.externalSpendLabel}}
                </span>
                <span class="text-gray-500" data-test-legend-external-amount>
                  {{#if this.isCreditBased}}
                    ({{this.formatCredits this.externalAmount}})
                  {{else}}
                    (<CurrencySuperscriptCents @amountCents={{this.externalAmount}} />)
                  {{/if}}
                </span>
              </button>
            </:trigger>

            <:body>
              <div class="p-4 min-w-64">
                {{#if this.hasPartnerUrl}}
                  <a
                    href={{this.partnerUrlString}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="tio-anchor"
                  >{{this.partnerName}}</a>
                {{else}}
                  <span class="text-gray-700 font-medium">{{this.partnerName}}</span>
                {{/if}}
                {{#if this.externalSpendItems.length}}
                  <div class="flex flex-col gap-2">
                    {{#each this.externalSpendItems as |item|}}
                      <div class="flex justify-between text-sm">
                        <span class="text-gray-700">{{item.description}}</span>
                        <span class="text-gray-900 font-medium">
                          <CurrencySuperscriptCents @amountCents={{item.amount}} />
                        </span>
                      </div>
                    {{/each}}
                  </div>
                {{else}}
                  <div class="text-sm text-gray-500">
                    {{t
                      "tas.dashboard.annual_benefits.external_usage_description"
                      partner=this.partnerName
                    }}
                    {{#if this.hasPartnerUrl}}
                      <a
                        href={{this.partnerUrlString}}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="tio-anchor"
                      >{{this.partnerName}}</a>
                    {{/if}}
                  </div>
                  <div class="flex justify-between text-sm mt-2">
                    <span class="text-gray-700">{{t "tuition_assistance.total"}}</span>
                    <span class="text-gray-900 font-medium">
                      {{#if this.isCreditBased}}
                        {{this.formatCredits this.externalAmount}}
                      {{else}}
                        <CurrencySuperscriptCents @amountCents={{this.externalAmount}} />
                      {{/if}}
                    </span>
                  </div>
                {{/if}}
              </div>
            </:body>
          </Popover>
        {{/if}}
      </div>

      {{#unless this.hasAnySpend}}
        <p class="text-sm text-gray-500 italic" data-test-no-spend-message>
          {{t "tas.dashboard.annual_benefits.no_spend"}}
        </p>
      {{/unless}}
    </div>
  </template>
}

export default TasDashboardAnnualBenefits;
